import React, { useEffect, useState } from 'react';
import styles from "./ridedetails.module.css";
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faLocationDot,
    faUserTie,
    faUser,
    faClock,
    faCar,
    faMoneyBillWave,
    faMapLocationDot,
    faStar,
    faTriangleExclamation,
    faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";

const groupIcons = {
    "Trip Details": faLocationDot,
    "Driver Details": faUserTie,
    "Passenger Details": faUser,
    "Date & Time": faClock,
    "Cab Details": faCar,
    "Payment Details": faMoneyBillWave,
};

const statusStyles = {
    completed: styles.statusCompleted,
    cancelled: styles.statusCancelled,
    ongoing: styles.statusOngoing,
};

const RideDetails = () => {
    const [details, setDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const trip_id = id;
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRideDetails = async () => {
            try {
                setLoading(true);
                const url = `${process.env.REACT_APP_URL}/api/rides/fetchRideDetails?trip_id=${trip_id}`;
                const response = await axios.get(url, { withCredentials: true });
                setDetails(response.data.rideDetails);
            } catch (error) {
                console.log("API correct??", error);
            } finally {
                setLoading(false);
            }
        };
        fetchRideDetails();
    }, [trip_id]);

    const handleBack = () => navigate(-1);

    const groupingConfig = {
        "Trip Details": ["request_id", "source", "destination", 'approx_price'],
        "Driver Details": ["driver_name", "driver_contact", "driver_ratings"],
        "Cab Details": ["cab_id", "cab_number", "medium_type", "service_type"],
        "Passenger Details": ["user_name", "user_contact", "user_email", "user_ratings"],
        "Payment Details": ["payment_id", "gateway", "amount", "status"],
        "Date & Time": ["trip_date", "trip_start_time", "trip_completed_time", "payment_date"],
    };

    const groupedDetails = Object.entries(groupingConfig).map(([title, keys]) => ({
        title,
        fields: keys.map(key => ({
            label: key.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase()),
            key,
            suffix: key === "total_amount" || key === "approx_price" ? " Rs" : "",
        })),
    }));

    const originLat = details.picklatitude;
    const originLng = details.picklongitude;
    const destLat = details.droplatitude;
    const destLng = details.droplongitude;

    const statusKey = (details.completion_status || "").toLowerCase();
    const statusClass = statusStyles[statusKey] || styles.statusDefault;

    // Adjust this check to match whatever shape your API actually sends
    // (boolean, 0/1, "Yes"/"No", etc.)
    const isInRideCancellation = (val) =>
        val === true || val === 1 || val === "1" || String(val).toLowerCase() === "yes";

    const handleDriverClick = () => {
        if (!details.driver_ID) return;
        navigate(`/drivers/details/${details.driver_ID}`);
    };

    const renderValue = (key) => {
        if (key === "driver_ratings" || key === "user_ratings") {
            const val = details[key];
            if (!val) return "null";
            return (
                <span className={styles.ratingWrap}>
                    {val}
                    <FontAwesomeIcon icon={faStar} className={styles.ratingStar} />
                </span>
            );
        }
        const val = details[key];
        const field = groupedDetails
            .flatMap(g => g.fields)
            .find(f => f.key === key);
        return val ? `${val}${field?.suffix || ""}` : "null";
    };

    // Per-group empty-state config. Each entry defines when a group should
    // show a short explanatory message instead of its field rows.
    const emptyStateConfig = {
        "Payment Details": {
            isEmpty: () => statusKey === "cancelled",
            message: "No payment was processed — this trip was cancelled.",
        },
    };

    return (
        <div className={styles.container}>
            <div className={styles.heading}>
                <button type="button" className={styles.bbtn} onClick={handleBack}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <div className={styles.headingText}>
                    <span className={styles.eyebrow}>Trip ID</span>
                    <h3 className={styles.hea4}>{trip_id}</h3>
                </div>

                <button
                    className={styles.mapbtn}
                    onClick={() =>
                        window.open(
                            `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${destLat},${destLng}`,
                            '_blank'
                        )
                    }
                >
                    <FontAwesomeIcon icon={faMapLocationDot} />
                    Open in Maps
                </button>
            </div>

            {loading ? (
                <div className={styles.skeletonGrid}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className={styles.skeletonCard} />
                    ))}
                </div>
            ) : (
                <div className={styles.detailsContainer}>
                    {groupedDetails.map((group, index) => {
                        const isTripGroup = group.title === "Trip Details";
                        const isDriverGroup = group.title === "Driver Details";
                        const driverClickable = isDriverGroup && !!details.driver_ID;

                        const emptyState = emptyStateConfig[group.title];
                        const showEmptyState = emptyState?.isEmpty();

                        return (
                            <div
                                key={index}
                                className={`${styles.selectedDetails} ${driverClickable ? styles.clickableCard : ""} ${showEmptyState ? styles.emptyCard : ""}`}
                                onClick={driverClickable ? handleDriverClick : undefined}
                                role={driverClickable ? "button" : undefined}
                                tabIndex={driverClickable ? 0 : undefined}
                                onKeyDown={
                                    driverClickable
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleDriverClick();
                                            }
                                        }
                                        : undefined
                                }
                            >
                                <div className={styles.groupHeaderRow}>
                                    <h3 className={styles.head3}>
                                        <FontAwesomeIcon icon={groupIcons[group.title]} className={styles.head3Icon} />
                                        {group.title}
                                    </h3>

                                    {isTripGroup && details.completion_status && (
                                        <div className={styles.statusInline}>
                                            <span className={`${styles.statusBadge} ${statusClass}`}>
                                                {details.completion_status}
                                            </span>

                                            {statusKey === "cancelled" && isInRideCancellation(details.in_ride_cancellation) && (
                                                <span className={styles.cancelChip}>
                                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                                    Mid-ride By <strong>{details.cancelled_by}</strong>
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {driverClickable && (
                                        <FontAwesomeIcon icon={faChevronRight} className={styles.cardChevron} />
                                    )}
                                </div>

                                {showEmptyState ? (
                                    <div className={styles.emptyState}>
                                        <FontAwesomeIcon icon={faCircleXmark} className={styles.emptyStateIcon} />
                                        <span>{emptyState.message}</span>
                                    </div>
                                ) : (
                                    group.fields.map((field, i) => (
                                        <span key={i} className={styles.det}>
                                            <strong>{field.label}</strong>
                                            <span>{renderValue(field.key)}</span>
                                        </span>
                                    ))
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RideDetails;